import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/Layout/DashboardLayout';
import ArrowLeftIcon from '../../components/icons/ArrowLeftIcon';
import PencilFillIcon from '../../components/icons/PencilFillIcon'; // Assuming you have this or similar
import { useAuth } from '../../contexts/AuthContext';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import AlertMessage from '../../components/ui/AlertMessage';

// Helper to get category name (you might want to move this to a utility file or fetch categories)
const getCategoryName = (categoryId, categories) => {
    // This is a placeholder. In a real app, you'd likely have a categories list
    // or fetch the category details.
    if (!categories || categories.length === 0) {
        // Fallback if categories are not loaded, or fetch them
        const cats = { '1': 'Hospedaje', '2': 'Alimentación', '3': 'Transporte', '4': 'Experiencias', '5': 'Guías y Tours', '6': 'Otros' };
        return cats[String(categoryId)] || 'Desconocida';
    }
    const category = categories.find(cat => String(cat.id) === String(categoryId) || String(cat.form_value) === String(categoryId));
    return category ? category.name : 'Desconocida';
};

const ServiceDetailSupplierScreen = () => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const { serviceId } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();

    const [service, setService] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [categories, setCategories] = useState([]); // To store category list for names

    // Images grouped by category and input section, as in the form screen
    const [serviceImages, setServiceImages] = useState({});
    // Modal state for lightbox
    const [modalOpen, setModalOpen] = useState(false);
    const [modalImg, setModalImg] = useState(null);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/service-categories/`, {
                    headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
                });
                if (!response.ok) throw new Error('Failed to fetch categories');
                const data = await response.json();
                setCategories(data);
            } catch (err) {
                console.error("Error fetching categories for detail view:", err);
                // Not critical for detail view if names are hardcoded as fallback
            }
        };
        fetchCategories();
    }, [API_BASE_URL, token]);

    useEffect(() => {
        if (!serviceId || !token) {
            setIsLoading(false);
            setError("Información de servicio o autenticación no disponible.");
            return;
        }

        const fetchServiceDetails = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await fetch(`${API_BASE_URL}/api/services/${serviceId}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ message: `Error ${response.status}` }));
                    throw new Error(errorData.message || `Error al obtener detalles del servicio: ${response.status}`);
                }
                const data = await response.json();
                setService(data);
            } catch (err) {
                console.error("Error fetching service details:", err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        // Fetch images for the service, organized as in the form screen
        const fetchServiceImages = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/service-images/service/${serviceId}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (!response.ok) {
                    throw new Error(`Failed to fetch service images: ${response.status}`);
                }
                let images = await response.json();
                images = images.filter(img => !img.deleted_at);
                const groupedImages = {};
                images.forEach(img => {
                    const category = img.category || 'uncategorized';
                    const inputType = img.input_type || 'default';
                    if (!groupedImages[category]) groupedImages[category] = {};
                    if (!groupedImages[category][inputType]) groupedImages[category][inputType] = [];
                    groupedImages[category][inputType].push(img);
                });
                setServiceImages(groupedImages);
            } catch (err) {
                console.error("Error fetching service images:", err);
            }
        };

        fetchServiceDetails();
        fetchServiceImages();
    }, [serviceId, API_BASE_URL, token]);

    if (isLoading) {
        return <FormSpinnerOverlay isLoading={true} />;
    }

    if (error) {
        return <AlertMessage type="danger" message={error} />;
    }

    if (!service) {
        return <AlertMessage type="info" message="Servicio no encontrado." />;
    }

    const categoryName = getCategoryName(service.category_id, categories);
    const availabilityStatus = service.availability_status_id === 1 ? 'Disponible' : 'No disponible';
    const weeklyDays = service.weekly_availability ? (Array.isArray(service.weekly_availability) ? service.weekly_availability.join(', ') : service.weekly_availability.split(',').join(', ')) : 'No especificado';

    // Organize galleries as in the form screen
    // Each gallery is an array of images (with .image_url)
    const galleries = {
        hospedaje: {
            habitacionPhotos: (serviceImages.hospedaje && serviceImages.hospedaje.habitacionPhotos) || [],
            alojamientoPhotos: (serviceImages.hospedaje && serviceImages.hospedaje.alojamientoPhotos) || [],
            exterioresPhotos: (serviceImages.hospedaje && serviceImages.hospedaje.exterioresPhotos) || [],
        },
        alimentacion: {
            platoPhotos: (serviceImages.alimentacion && serviceImages.alimentacion.platoPhotos) || [],
            localPhotos: (serviceImages.alimentacion && serviceImages.alimentacion.localPhotos) || [],
            ambientePhotos: (serviceImages.alimentacion && serviceImages.alimentacion.ambientePhotos) || [],
        },
        transporte: {
            vehiculoInteriorPhotos: (serviceImages.transporte && serviceImages.transporte.vehiculoInteriorPhotos) || [],
            vehiculoExteriorPhotos: (serviceImages.transporte && serviceImages.transporte.vehiculoExteriorPhotos) || [],
        },
        experiencia: {
            actividadPhotos: (serviceImages.experiencia && serviceImages.experiencia.actividadPhotos) || [],
            entornoPhotos: (serviceImages.experiencia && serviceImages.experiencia.entornoPhotos) || [],
        },
        guias: {
            guiaProfilePhotos: (serviceImages.guias && serviceImages.guias.guiaProfilePhotos) || [],
            guiaServicioPhotos: (serviceImages.guias && serviceImages.guias.guiaServicioPhotos) || [],
        },
        otros: {
            otrosServicioPhotos: (serviceImages.otros && serviceImages.otros.otrosServicioPhotos) || [],
        },
        accesibilidad: {
            additionalPhotos: (serviceImages.accesibilidad && serviceImages.accesibilidad.additionalPhotos) || [],
        }
    };

    let featuresToList = [];
    if (service && service.category_id) {
        const categoryIdStr = String(service.category_id);
        if (categoryIdStr === '1' && service.selected_hosting_features) {
            featuresToList = service.selected_hosting_features;
        } else if (categoryIdStr === '2' && service.selected_food_options) {
            featuresToList = service.selected_food_options;
        } else if (categoryIdStr === '3' && service.selected_transport_options) {
            featuresToList = service.selected_transport_options;
        }
    }

    // Handlers for lightbox modal
    const handleOpenImageModal = (img) => {
        setModalImg(img);
        setModalOpen(true);
    };
    const handleCloseImageModal = () => {
        setModalOpen(false);
        setModalImg(null);
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-5">
                <div className="d-flex align-items-center">
                    <Link to="/services" className="btn btn-back-circle me-3" title="Volver a servicios">
                        <ArrowLeftIcon />
                    </Link>
                    <h2 className="mb-0">Volver a servicios</h2>
                </div>
            </div>

            {/* Main info: two-column layout */}
            <div className="row text-start mb-4">
                <div className="col-12">
                    <div className="card card-metric border-0">
                        <div className="card-body px-md-2 px-0">
                            <div className="row mb-3 gx-md-5">
                                {/* Left: main image or first gallery image */}
                                <div id="serviceDetailsGallery" className="col-md-6 text-start d-md-flex flex-md-column">
                                    <div className="row gx-3 gy-3 mt-3 mb-5">
                                        {(() => {
                                            // Show up to 4 images, each from a different input section of the selected category
                                            const categoryMap = {
                                                '1': { key: 'hospedaje', sections: ['habitacionPhotos', 'alojamientoPhotos', 'exterioresPhotos'] },
                                                '2': { key: 'alimentacion', sections: ['platoPhotos', 'localPhotos', 'ambientePhotos'] },
                                                '3': { key: 'transporte', sections: ['vehiculoInteriorPhotos', 'vehiculoExteriorPhotos'] },
                                                '4': { key: 'experiencia', sections: ['actividadPhotos', 'entornoPhotos'] },
                                                '5': { key: 'guias', sections: ['guiaProfilePhotos', 'guiaServicioPhotos'] },
                                                '6': { key: 'otros', sections: ['otrosServicioPhotos'] }
                                            };
                                            const catId = String(service.category_id);
                                            const catInfo = categoryMap[catId];
                                            let images = [];
                                            if (catInfo && galleries[catInfo.key]) {
                                                for (const section of catInfo.sections) {
                                                    const arr = galleries[catInfo.key][section];
                                                    if (arr && arr.length > 0) {
                                                        images.push(arr[0]);
                                                    }
                                                    if (images.length === 4) break;
                                                }
                                            }
                                            // Add accesibilidad/additionalPhotos if available and not already included
                                            if (
                                                galleries.accesibilidad &&
                                                galleries.accesibilidad.additionalPhotos &&
                                                galleries.accesibilidad.additionalPhotos.length > 0
                                            ) {
                                                const alreadyIncluded = images.some(
                                                    img => img.id === galleries.accesibilidad.additionalPhotos[0].id
                                                );
                                                if (!alreadyIncluded && images.length < 4) {
                                                    images.push(galleries.accesibilidad.additionalPhotos[0]);
                                                }
                                            }
                                            if (images.length === 0) {
                                                return (
                                                    <div className="col-12">
                                                        <img src="https://placehold.co/600x300/EFEFEF/AAAAAA&text=Service+Main" className="img-fluid w-100" alt="Imagen principal del servicio" />
                                                    </div>
                                                );
                                            }
                                            return (
                                                <>
                                                    <div className="col-12">
                                                        <img
                                                            src={images[0].image_url}
                                                            className="img-fluid w-100"
                                                            alt="Imagen principal del servicio"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => handleOpenImageModal(images[0])}
                                                        />
                                                    </div>
                                                    {images.slice(1, 4).map((img, idx) => (
                                                        <div className="col-md-4" key={`main-gallery-${img.id}`}>
                                                            <img
                                                                src={img.image_url}
                                                                className="img-fluid"
                                                                alt={`Imagen de la galería ${idx + 2}`}
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => handleOpenImageModal(img)}
                                                            />
                                                        </div>
                                                    ))}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                                {/* Right: service info */}
                                <div id="serviceDetailsResume" className="col-md-6 text-start d-flex flex-column justify-content-center align-items-start">
                                    <h1 className="h2 mb-3">{service.name}</h1>
                                    <h4>Descripción del Servicio</h4>
                                    <p className="text-muted mb-4">{service.description || "No hay descripción disponible."}</p>
                                    <span className={`badge ${availabilityStatus === 'Disponible' ? 'bg-success-subtle text-success-emphasis' : 'bg-light text-dark'} fs-6 fw-normal mb-3 d-inline-flex align-items-center`}>
                                        <strong className="me-2">Disponibilidad: </strong>
                                        <i className={`bi ${availabilityStatus === 'Disponible' ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>
                                        <span>{availabilityStatus}</span>
                                    </span>
                                    {service.price && (
                                        <span className="badge bg-light text-dark fs-6 fw-normal mb-2 d-inline-flex align-items-center">
                                            <strong>Precio: </strong>
                                            <i className="bi bi-currency-dollar ms-2 me-1"></i>
                                            <span>{parseFloat(service.price).toFixed(2)} USD {service.price_description || ''}</span>
                                        </span>
                                    )}
                                    {service.capacity && (
                                        <span className="badge bg-light text-dark fs-6 fw-normal mb-4 d-inline-flex align-items-center">
                                            <strong>Capacidad: </strong>
                                            <i className="bi bi-people-fill ms-2 me-1"></i>
                                            <span>{service.capacity} personas</span>
                                        </span>
                                    )}
                                    <Link to={`/services/edit/${service.id}`} className="btn btn-checkin mt-3">
                                        <PencilFillIcon className="me-2" width="16" height="16" fill="white" />
                                        Editar Servicio
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Features */}
            {featuresToList.length > 0 && (
                <div className="row text-start mb-4">
                    <div className="col-12">
                        <div className="card card-metric border-0">
                            <div className="card-body px-md-2 px-0">
                                <h2 className="h4 mb-5">Características del Servicio ({categoryName})</h2>
                                <div className="row">
                                    {featuresToList.map(feature => (
                                        <div className="col-md-4 mb-2" key={feature.id}>
                                            <p><i className="bi bi-check-lg text-success me-2"></i>{feature.name}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Conditions */}
            <div className="row text-start mb-4">
                <div className="col-12">
                    <div className="card card-metric border-0">
                        <div className="card-body px-md-2 px-0">
                            <h2 className="h4 mb-3">Condiciones del Servicio</h2>
                            {service.duration && <p><strong>Duración del Servicio:</strong> {service.duration} {service.duration === 1 ? 'hora' : 'horas'}</p>}
                            {(service.start_time || service.end_time) && <p><strong>Horario:</strong> {service.start_time || 'N/A'} a {service.end_time || 'N/A'}</p>}
                            <p><strong>Disponibilidad Semanal:</strong> {weeklyDays}</p>
                            {service.additional_conditions && <p><strong>Condiciones Adicionales:</strong> {service.additional_conditions}</p>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Accessibility Evidence (description and images) */}
            {(service.accessibility_info || galleries.accesibilidad.additionalPhotos.length > 0) && (
                <div id="accesibilityEvidence" className="row text-start mb-4">
                    <div className="col-12">
                        <div className="card card-metric border-0">
                            <div className="card-body px-md-2 px-0">
                                <h2 className="h4 mb-3">Evidencia de Accesibilidad y Adaptaciones</h2>
                                {galleries.accesibilidad.additionalPhotos.length > 0 && (
                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                        {galleries.accesibilidad.additionalPhotos.map(img => (
                                            <img
                                                key={img.id}
                                                src={img.image_url}
                                                alt="Accesibilidad"
                                                className="img-thumbnail"
                                                style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                                onClick={() => handleOpenImageModal(img)}
                                            />
                                        ))}
                                    </div>
                                )}
                                {service.accessibility_info && (
                                    <p className="text-start mb-0">
                                        <strong>Descripción de Accesibilidad:</strong> {service.accessibility_info}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* All galleries at the end */}
            {/* Hospedaje */}
            {Object.values(galleries.hospedaje).some(arr => arr.length > 0) && (
                <div className="row text-start mb-4">
                    <div className="col-12">
                        <div className="card card-metric border-0">
                            <div className="card-body px-md-2 px-0">
                                <h2 className="h4 mb-3">Galería de Hospedaje</h2>
                                <div className="row">
                                    {galleries.hospedaje.habitacionPhotos.length > 0 && (
                                        <div className="col-12 mb-3">
                                            <h5>Fotos de la habitación</h5>
                                            <div className="d-flex flex-wrap gap-2">
                                                {galleries.hospedaje.habitacionPhotos.map(img => (
                                                    <img
                                                        key={img.id}
                                                        src={img.image_url}
                                                        alt="Habitación"
                                                        className="img-thumbnail"
                                                        style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handleOpenImageModal(img)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {galleries.hospedaje.alojamientoPhotos.length > 0 && (
                                        <div className="col-12 mb-3">
                                            <h5>Fotos del alojamiento</h5>
                                            <div className="d-flex flex-wrap gap-2">
                                                {galleries.hospedaje.alojamientoPhotos.map(img => (
                                                    <img
                                                        key={img.id}
                                                        src={img.image_url}
                                                        alt="Alojamiento"
                                                        className="img-thumbnail"
                                                        style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handleOpenImageModal(img)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {galleries.hospedaje.exterioresPhotos.length > 0 && (
                                        <div className="col-12 mb-3">
                                            <h5>Fotos exteriores</h5>
                                            <div className="d-flex flex-wrap gap-2">
                                                {galleries.hospedaje.exterioresPhotos.map(img => (
                                                    <img
                                                        key={img.id}
                                                        src={img.image_url}
                                                        alt="Exteriores"
                                                        className="img-thumbnail"
                                                        style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handleOpenImageModal(img)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Alimentación */}
            {Object.values(galleries.alimentacion).some(arr => arr.length > 0) && (
                <div className="row text-start mb-4">
                    <div className="col-12">
                        <div className="card card-metric border-0">
                            <div className="card-body px-md-2 px-0">
                                <h2 className="h4 mb-3">Galería de Alimentación</h2>
                                <div className="row">
                                    {galleries.alimentacion.platoPhotos.length > 0 && (
                                        <div className="col-12 mb-3">
                                            <h5>Fotos del Plato/Producto</h5>
                                            <div className="d-flex flex-wrap gap-2">
                                                {galleries.alimentacion.platoPhotos.map(img => (
                                                    <img
                                                        key={img.id}
                                                        src={img.image_url}
                                                        alt="Plato"
                                                        className="img-thumbnail"
                                                        style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handleOpenImageModal(img)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {galleries.alimentacion.localPhotos.length > 0 && (
                                        <div className="col-12 mb-3">
                                            <h5>Fotos del Local/Restaurante</h5>
                                            <div className="d-flex flex-wrap gap-2">
                                                {galleries.alimentacion.localPhotos.map(img => (
                                                    <img
                                                        key={img.id}
                                                        src={img.image_url}
                                                        alt="Local"
                                                        className="img-thumbnail"
                                                        style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handleOpenImageModal(img)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {galleries.alimentacion.ambientePhotos.length > 0 && (
                                        <div className="col-12 mb-3">
                                            <h5>Fotos del Ambiente</h5>
                                            <div className="d-flex flex-wrap gap-2">
                                                {galleries.alimentacion.ambientePhotos.map(img => (
                                                    <img
                                                        key={img.id}
                                                        src={img.image_url}
                                                        alt="Ambiente"
                                                        className="img-thumbnail"
                                                        style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handleOpenImageModal(img)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Transporte */}
            {Object.values(galleries.transporte).some(arr => arr.length > 0) && (
                <div className="row text-start mb-4">
                    <div className="col-12">
                        <div className="card card-metric border-0">
                            <div className="card-body px-md-2 px-0">
                                <h2 className="h4 mb-3">Galería de Transporte</h2>
                                <div className="row">
                                    {galleries.transporte.vehiculoInteriorPhotos.length > 0 && (
                                        <div className="col-12 mb-3">
                                            <h5>Fotos del Vehículo (Interior)</h5>
                                            <div className="d-flex flex-wrap gap-2">
                                                {galleries.transporte.vehiculoInteriorPhotos.map(img => (
                                                    <img
                                                        key={img.id}
                                                        src={img.image_url}
                                                        alt="Vehículo Interior"
                                                        className="img-thumbnail"
                                                        style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handleOpenImageModal(img)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {galleries.transporte.vehiculoExteriorPhotos.length > 0 && (
                                        <div className="col-12 mb-3">
                                            <h5>Fotos del Vehículo (Exterior)</h5>
                                            <div className="d-flex flex-wrap gap-2">
                                                {galleries.transporte.vehiculoExteriorPhotos.map(img => (
                                                    <img
                                                        key={img.id}
                                                        src={img.image_url}
                                                        alt="Vehículo Exterior"
                                                        className="img-thumbnail"
                                                        style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handleOpenImageModal(img)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Experiencia */}
            {Object.values(galleries.experiencia).some(arr => arr.length > 0) && (
                <div className="row text-start mb-4">
                    <div className="col-12">
                        <div className="card card-metric border-0">
                            <div className="card-body px-md-2 px-0">
                                <h2 className="h4 mb-3">Galería de Experiencia</h2>
                                <div className="row">
                                    {galleries.experiencia.actividadPhotos.length > 0 && (
                                        <div className="col-12 mb-3">
                                            <h5>Fotos de la Actividad</h5>
                                            <div className="d-flex flex-wrap gap-2">
                                                {galleries.experiencia.actividadPhotos.map(img => (
                                                    <img
                                                        key={img.id}
                                                        src={img.image_url}
                                                        alt="Actividad"
                                                        className="img-thumbnail"
                                                        style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handleOpenImageModal(img)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {galleries.experiencia.entornoPhotos.length > 0 && (
                                        <div className="col-12 mb-3">
                                            <h5>Fotos del Entorno/Lugar</h5>
                                            <div className="d-flex flex-wrap gap-2">
                                                {galleries.experiencia.entornoPhotos.map(img => (
                                                    <img
                                                        key={img.id}
                                                        src={img.image_url}
                                                        alt="Entorno"
                                                        className="img-thumbnail"
                                                        style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handleOpenImageModal(img)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Guías */}
            {Object.values(galleries.guias).some(arr => arr.length > 0) && (
                <div className="row text-start mb-4">
                    <div className="col-12">
                        <div className="card card-metric border-0">
                            <div className="card-body px-md-2 px-0">
                                <h2 className="h4 mb-3">Galería de Guías y Tours</h2>
                                <div className="row">
                                    {galleries.guias.guiaProfilePhotos.length > 0 && (
                                        <div className="col-12 mb-3">
                                            <h5>Fotos del Guía/Acompañante</h5>
                                            <div className="d-flex flex-wrap gap-2">
                                                {galleries.guias.guiaProfilePhotos.map(img => (
                                                    <img
                                                        key={img.id}
                                                        src={img.image_url}
                                                        alt="Guía"
                                                        className="img-thumbnail"
                                                        style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handleOpenImageModal(img)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {galleries.guias.guiaServicioPhotos.length > 0 && (
                                        <div className="col-12 mb-3">
                                            <h5>Fotos del Servicio/Actividad</h5>
                                            <div className="d-flex flex-wrap gap-2">
                                                {galleries.guias.guiaServicioPhotos.map(img => (
                                                    <img
                                                        key={img.id}
                                                        src={img.image_url}
                                                        alt="Servicio/Actividad"
                                                        className="img-thumbnail"
                                                        style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => handleOpenImageModal(img)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Otros */}
            {galleries.otros.otrosServicioPhotos.length > 0 && (
                <div className="row text-start mb-4">
                    <div className="col-12">
                        <div className="card card-metric border-0">
                            <div className="card-body px-md-2 px-0">
                                <h2 className="h4 mb-3">Galería de Otros Servicios/Productos</h2>
                                <div className="d-flex flex-wrap gap-2">
                                    {galleries.otros.otrosServicioPhotos.map(img => (
                                        <img
                                            key={img.id}
                                            src={img.image_url}
                                            alt="Otros Servicio/Producto"
                                            className="img-thumbnail"
                                            style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                            onClick={() => handleOpenImageModal(img)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {/* Old accessibility evidence block removed: now handled by new galleries logic */}

            {/* Modal for lightbox */}
            {modalOpen && modalImg && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.7)',
                        zIndex: 2000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={handleCloseImageModal}
                >
                    <div
                        style={{
                            position: 'relative',
                            background: '#fff',
                            padding: 16,
                            borderRadius: 8,
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            boxShadow: '0 2px 16px rgba(0,0,0,0.3)'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <img
                            src={modalImg.image_url || modalImg.url}
                            alt="Preview"
                            style={{ maxWidth: '80vw', maxHeight: '70vh', display: 'block', margin: '0 auto' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                            <button className="btn btn-secondary" onClick={handleCloseImageModal}>
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ServiceDetailSupplierScreen;